import React, { useState, useCallback, useMemo, useEffect } from 'react';
import cn from 'classnames';
import Api from 'api';
import Container from 'components/UI/Container';
import Loader from 'components/UI/Loader';
import Slider from 'components/UI/Slider';
import { CustomDot, CustomPagination } from 'components/UI/Slider/utils';
import { useTrans } from 'utils/localisation';
import { pushCustomEvent } from 'utils/analytics';
import NewsItem from './NewsItem';
import { NEWS_STORAGE_KEY } from 'define/news';

import * as styles from './News.module.scss';

export default function News({ className }) {
  const t = useTrans(['common']);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [draggable, setDraggable] = useState(true);

  const lastSlide = {
    id: 0,
    title: t('wantToKnowMore'),
    link: '/company/news',
    linkText: t('seeAll'),
  };
  const preparedNews = [...news, lastSlide];

  useEffect(() => {
    const cachedNews = JSON.parse(sessionStorage.getItem(NEWS_STORAGE_KEY)) ?? [];

    if (cachedNews.length) {
      setNews(cachedNews);
      setLoading(false);
    } else {
      Api.getNews()
        .then(res => {
          setNews(res);
          sessionStorage.setItem(NEWS_STORAGE_KEY, JSON.stringify(res));
        })
        .finally(() => setLoading(false));
    }
  }, []);

  const handlePushEvent = (action, label) => {
    let options = { action };

    if (label) {
      options = { action, label };
    }
    pushCustomEvent('ndrsn_news', options);
  };

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const sliderSettings = useMemo(
    () => ({
      customPaging: CustomDot.bind(null, preparedNews.length, current / 3),
      appendDots: CustomPagination,
      dotsClass: 'slick-pagination',
      dots: false,
      slidesToShow: 3,
      slidesToScroll: 3,
      arrows: true,
      infinite: false,
      draggable,
      swipe: draggable,
      responsive: [
        {
          breakpoint: 1025,
          settings: {
            arrows: false,
            dots: true,
          },
        },
        {
          breakpoint: 544,
          settings: {
            customPaging: CustomDot.bind(null, preparedNews.length, current),
            slidesToShow: 1,
            slidesToScroll: 1,
            variableWidth: true,
            arrows: false,
            dots: true,
          },
        },
      ],
    }),
    [preparedNews.length, current, draggable],
  );

  return (
    <Container background="transparent" className={cn(styles.container, className)}>
      <div className={styles.layout}>
        {loading ? (
          <Loader theme="fullHeight" />
        ) : (
          <div className="defaultSliderDarkBg">
            <Slider
              {...sliderSettings}
              beforeChange={setCurrentKey}
              afterChange={() => handlePushEvent('slide_change', 'intro_slider')}
              className={styles.slider}
            >
              {preparedNews?.map((item, i) => (
                <NewsItem
                  key={item.id}
                  item={item}
                  handlePushEvent={handlePushEvent}
                  setDraggable={setDraggable}
                  isLast={preparedNews.length === i + 1}
                />
              ))}
            </Slider>
          </div>
        )}
      </div>
    </Container>
  );
}
